import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { CLIENT_SURFACE, ClientSurfaceEnum, CONSTANTS, Constants, ENV, Environment } from '../../commons/injection.token';

/** A refused (re)connection — the gateway's `connect_error`. */
export interface SocketConnectError {
  /** i18n key the gateway attached (`chat.errors.sessionExpired`…), when any. */
  errorKey?: string;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class SocketService implements OnDestroy {

  private socket: Socket | undefined = undefined;

  private connected$ = new BehaviorSubject<boolean>(false);

  private readonly connectErrors = new Subject<SocketConnectError>();

  /**
   * Refused connection attempts. A chat app socket with an expired token is refused
   * with `chat.errors.sessionExpired` (SPEC-2026-09-internal-chat-pwa) — its session
   * runtime refreshes the token and calls `reconnect()`.
   */
  readonly connectErrors$: Observable<SocketConnectError> = this.connectErrors.asObservable();

  constructor(
    @Inject(ENV) private environment: Environment,
    // Optional so apps that do not provide CONSTANTS keep working — without it
    // the handshake simply carries no token (legacy unauthenticated behavior).
    @Optional() @Inject(CONSTANTS) private constants: Constants | null,
    @Optional() @Inject(CLIENT_SURFACE) private surface: ClientSurfaceEnum | null,
  ) { }

  private joinedEvents = new Set<string>();

  private get socketActived() {
    return (this.environment['socketActived'] === true);
  }

  public getConnectedStatus(): Observable<boolean> {
    return this.connected$.asObservable();
  }

  public isConnected(): boolean {
    return this.connected$.value;
  }

  /**
   * Optional JWT for the chat handshake (SPEC-2026-07-internal-chat, ADR-002).
   * Reads the token from the same storage AuthService writes
   * (`localStorage[constants.currentUser] = { token, user }`). No token →
   * empty auth → the gateway keeps the connection unauthenticated and every
   * legacy `event_*` behavior is unchanged.
   *
   * With a token it also declares the surface and a device-class hint
   * (SPEC-2026-09-internal-chat-pwa): the gateway refuses a chat app socket whose
   * token is invalid instead of degrading it to anonymous, and counts the daily
   * surface activity. Identity itself always comes from the verified token.
   */
  private buildHandshakeAuth(): Record<string, unknown> {
    try {
      const storageKey = this.constants?.['currentUser'];
      if (!storageKey) return {};
      const stored = localStorage.getItem(storageKey);
      if (!stored) return {};
      const token = JSON.parse(stored)?.token;
      return token
        ? { token, surface: this.surface ?? ClientSurfaceEnum.WEB_APP, platform: this.resolvePlatform() }
        : {};
    } catch {
      return {};
    }
  }

  /** DESKTOP | MOBILE — a metric hint, never an authorization input. */
  private resolvePlatform(): 'DESKTOP' | 'MOBILE' {
    try {
      return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches ? 'MOBILE' : 'DESKTOP';
    } catch {
      return 'DESKTOP';
    }
  }

  public connect() {
    if (this.socketActived && !this.socket) {
      this.socket = io(String(this.environment['trendWebsocketUrl']), {
        reconnection: true,
        transports: ["websocket"],
        // Function form: evaluated on EVERY (re)connection attempt, so the
        // handshake always carries the CURRENT token (login/logout between
        // reconnects is picked up automatically).
        auth: (cb) => cb(this.buildHandshakeAuth()),
      });
      this.socket.on('connect', () => {
        this.connected$.next(true);
        this.joinedEvents.forEach(event => {
          this.socket!.emit('join', { event });
        });
      });
      this.socket.on('disconnect', () => this.connected$.next(false));
      this.socket.on('connect_error', (error: Error & { data?: { errorKey?: string } }) => {
        this.connectErrors.next({ errorKey: error?.data?.errorKey, message: error?.message ?? '' });
      });
      this.connected$.asObservable().subscribe(connected => {
        console.log('Socket connected: ', connected);
      });
    }
  }

  public disconnect() {
    if (this.socketActived && this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected$.next(false);
    }
  }

  /**
   * Drops the current connection and opens a new one on the same socket, keeping
   * every joined room (they are re-joined on `connect`) and every `listen()`
   * subscription. The handshake is rebuilt, so a refreshed token is picked up.
   * Needed after the gateway itself closed the socket (expired or revoked session):
   * socket.io never reconnects on its own after a server-side disconnect.
   */
  public reconnect() {
    if (!this.socketActived) return;
    if (!this.socket) {
      this.connect();
      return;
    }
    this.socket.disconnect();
    this.socket.connect();
  }

  /**
   * Joins `event` and keeps it in the re-join set.
   *
   * Joining BEFORE connect() is legitimate and must not be lost: the chat app opens its
   * socket only after renewing the token (an HTTP round-trip), while the chat shell mounts
   * and joins its personal/presence rooms in the same tick — so every one of those joins
   * used to be dropped silently and never retried, and no chat fan-out (unread, acks,
   * presence, surface) ever reached that surface. The room is therefore always remembered
   * and emitted on the next `connect`, which is also what covers reconnects.
   */
  public joinEvent(event: string) {
    this.joinedEvents.add(event);
    if (!this.socket?.connected) return;

    this.socket.emit('join', { event });
  }

  /**
   * Stops re-joining `event` after reconnects. Socket.io rooms are
   * server-managed, so the current socket stays in the room until it
   * disconnects — callers must additionally unsubscribe their listeners.
   */
  public leaveEvent(event: string) {
    this.joinedEvents.delete(event);
  }

  /**
   * Raw emit used by domain wrappers (e.g. ChatSocketService for
   * `chat:command` / `chat:typing`). Returns false when the socket is off or
   * disconnected so callers can queue and resend — socket.io's internal
   * buffering is intentionally bypassed to keep FIFO control client-side.
   */
  public emit(event: string, data: unknown): boolean {
    if (!this.socketActived || !this.socket || !this.socket.connected) {
      return false;
    }
    this.socket.emit(event, data);
    return true;
  }

  public listen(event: string): Observable<any> {
    if (!this.socketActived) {
      return new Observable();
    }
    return new Observable(observer => {
      const handler = (data: any) => observer.next(data);
      // (Re)bind the handler whenever a connection is (re)established — covers
      // listen() being subscribed BEFORE connect() and the socket being re-created.
      // Without this, an early subscription returns a dead stream and the UI never
      // updates even though the server emits to the room.
      const sub = this.connected$.subscribe(connected => {
        if (connected && this.socket) {
          this.socket.off(event, handler);
          this.socket.on(event, handler);
        }
      });
      return () => {
        sub.unsubscribe();
        this.socket?.off(event, handler);
      };
    });
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

}